import axios from 'axios'
import {
  OrderAddress,
  OrderBuyer,
  OrderDetail,
  OrderItem,
  OrderListItem,
  OrderStatus,
  StatusHistoryEntry,
  normalizeOrderStatus,
} from '../../domain/entities/Order'
import { GetOrdersFilters, GetOrdersResponse, OrderRepository } from '../../domain/repositories/OrderRepository'
import {
  OrderAddressApi,
  OrderBuyerApi,
  OrderDetailApi,
  OrderItemApi,
  OrderListItemApi,
  StatusHistoryEntryApi,
  orderApiDatasource,
} from '../datasources/OrderApiDatasource'

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined
    return data?.message || data?.error || fallback
  }

  return err instanceof Error ? err.message : fallback
}

const toAddress = (a: OrderAddressApi): OrderAddress => ({
  street: a.street,
  city: a.city,
  state: a.state,
  zip: a.zip,
})

const toBuyer = (b: OrderBuyerApi): OrderBuyer => ({
  id: b.id,
  name: b.name,
  businessName: b.business_name,
  phone: b.phone,
  email: b.email,
  role: b.role,
})

const toItem = (i: OrderItemApi): OrderItem => ({
  id: i.id,
  catalogItemId: i.catalog_item_id,
  productName: i.product_name,
  variant: i.variant,
  unit: i.unit,
  units: i.units,
  qty: i.qty,
  unitPrice: i.unit_price,
  subtotal: i.subtotal,
})

const toListItem = (o: OrderListItemApi): OrderListItem | null => {
  const status = normalizeOrderStatus(o.status)
  if (!status) return null
  const items = o.items ? o.items.map(toItem) : undefined
  const totalUnits = items ? items.reduce((sum, item) => sum + item.units, 0) : undefined

  return {
    id: o.id,
    status,
    version: o.version,
    paymentMode: o.payment_mode,
    orderPlacedDate: o.order_placed_date,
    requestedDeliveryDate: o.requested_delivery_date,
    totalAmount: o.total_amount,
    billToAddress: toAddress(o.bill_to_address),
    shipToAddress: toAddress(o.ship_to_address),
    buyer: o.buyer ? toBuyer(o.buyer) : undefined,
    paymentStatus: o.payment_status,
    moqCheck: o.moq_check,
    isRepeat: o.is_repeat,
    hasGrievance: o.has_grievance ?? false,
    items,
    totalUnits,
    createdAt: o.created_at,
    updatedAt: o.updated_at,
  }
}

const toStatusHistory = (h: StatusHistoryEntryApi): StatusHistoryEntry | null => {
  const status = normalizeOrderStatus(h.status)
  if (!status) return null
  return { status, changedAt: h.changed_at }
}

const toDetail = (d: OrderDetailApi): OrderDetail => {
  const status = normalizeOrderStatus(d.status)
  if (!status) throw new Error(`Unknown order status: ${d.status}`)
  return {
    id: d.id,
    status,
    version: d.version,
    moqCheck: d.moq_check,
    moqWarning: !!d.moq_warning,
    isRepeat: d.is_repeat,
    paymentMode: d.payment_mode,
    orderPlacedDate: d.order_placed_date,
    requestedDeliveryDate: d.requested_delivery_date,
    totalAmount: d.total_amount,
    remarks: d.remarks,
    billToAddress: toAddress(d.bill_to_address),
    shipToAddress: toAddress(d.ship_to_address),
    buyer: d.buyer ? toBuyer(d.buyer) : undefined,
    payments: d.payments.map((p) => ({
      id: p.id,
      amount: p.amount,
      paymentDate: p.payment_date,
      paymentMode: p.payment_mode,
      status: p.status,
      screenshotUrl: p.screenshot_url,
      verifiedAt: p.verified_at,
      rejectionReason: p.rejection_reason,
    })),
    hasGrievance: d.has_grievance ?? false,
    items: d.items.map(toItem),
    statusHistory: (d.status_history ?? []).map(toStatusHistory).filter((h): h is StatusHistoryEntry => h !== null),
    createdAt: d.created_at,
    updatedAt: d.updated_at,
  }
}

export class OrderRepositoryImpl implements OrderRepository {
  public async getOrders(filters: GetOrdersFilters): Promise<GetOrdersResponse> {
    try {
      const res = await orderApiDatasource.getOrders({
        skip: (filters.page - 1) * filters.limit,
        limit: filters.limit,
        status: filters.status,
        search: filters.search || undefined,
        moq_check: filters.moqCheck,
        catalog_item_id: filters.catalogItemId || undefined,
      })

      return {
        orders: res.data.map(toListItem).filter((o): o is OrderListItem => o !== null),
        totalItems: res.total,
        totalPages: Math.max(1, Math.ceil(res.total / filters.limit)),
      }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load orders.'))
    }
  }

  public async getOrderDetail(id: string): Promise<OrderDetail> {
    try {
      const detail = await orderApiDatasource.getOrderDetail(id)
      return toDetail(detail)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load order detail.'))
    }
  }

  public async getOrderById(id: string): Promise<OrderDetail | null> {
    try {
      const detail = await orderApiDatasource.getOrderDetail(id)
      return toDetail(detail)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      throw new Error(extractErrorMessage(err, 'Failed to load order.'))
    }
  }

  public async updateOrderStatus(id: string, status: OrderStatus, version: number): Promise<void> {
    try {
      await orderApiDatasource.updateOrderStatus(id, { status, version })
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to update order status.'))
    }
  }
}

export const orderRepository = new OrderRepositoryImpl()
